import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import DropZone from 'react-dropzone'
import { useDispatch } from 'react-redux'
import { checkSubscribersImport } from 'app/store/importCSV/helpers'
import {
  setCSVFileData,
  useImportCSVData,
  setInitilization,
} from 'app/store/importCSV'

import {
  cancelImportThunk,
  sendCSVFileDataToValidateThunk,
} from 'app/store/importCSV/importCSVExtraReducers'

import useFeatureFlag from 'app/configs/useFeatureFlag'
import { SECONDARY } from 'app/constants/button'
import {
  STEP_PROGRESS_TWO,
  LIMIT_CSV_IMPORT,
} from 'app/constants/subscriberCRM'

import MessageAlert from 'app/components/MessageAlert'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'

import DropZoneAreaBox from './DropZoneAreaBox'
import {
  generateRowsData,
  getInfoMappedHeadersInCSVFile,
  getParseCsvData,
  validationCSVFileHeaders,
  validationMaxCSVLimit,
  validationMaxCSVSize,
  formatBytes,
  hasValidCSVFile,
  isValidMissingRequiredFields,
} from './helpers'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import { useFlag } from '@unleash/proxy-client-react'

const CSVDropZoneBox = (props) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const { csv_exceeds_limit, mes_3653_limit_import_csv_300 } = useFeatureFlag()

  const { optionsLanguages } = useImportCSVData()

  const defaultLanguage = useDefaultLanguage()

  const { isDisplayCareCheck, toggleDialogs } = props

  const [isLimit, setIsLimit] = useState(false)
  const [dataLimitSize, setDataLimitSize] = useState(null)

  const showSnackbar = (msg) => {
    const secondsInmillisec = 10000
    dispatch(
      MessageAlert.showError({ message: msg, duration: secondsInmillisec }),
    )
  }

  const initialize = async () => {
    dispatch(setInitilization())
  }

  const handleUploadCSV = async (name, size, csvDataString) => {
    try {
      const csvBody = await getParseCsvData(csvDataString)
      hasValidCSVFile(csvBody, formatMessage)
      if (csv_exceeds_limit) {
        validationMaxCSVLimit(csvBody.length, formatMessage)
      }
      if (csvBody.length > 0) {
        const headerKeys = csvBody.shift()
        isValidMissingRequiredFields(headerKeys, formatMessage)
        const { validatedHeaderKeys, objWithIndex, headersOfCreatedByIndex } =
          validationCSVFileHeaders(headerKeys)
        const { infoMappedHeaders, isInvalidHeaderKey } =
          getInfoMappedHeadersInCSVFile(headerKeys)
        const { rows, filteredValidateHeaderKeys } = generateRowsData(
          csvBody,
          objWithIndex,
          optionsLanguages,
          defaultLanguage?.name,
          headersOfCreatedByIndex,
          validatedHeaderKeys,
        )
        const CSVData = {
          data: {
            headers: filteredValidateHeaderKeys,
            rows,
            fileName: name,
            fileSize: formatBytes(size),
            infoMappedHeaders,
          },
          activeStep: STEP_PROGRESS_TWO,
        }
        if (isInvalidHeaderKey) {
          dispatch(setCSVFileData(CSVData))
        } else {
          dispatch(sendCSVFileDataToValidateThunk(CSVData))
        }
      }
    } catch (err) {
      if (typeof err === 'string') showSnackbar(err)
    }
  }

  const acceptedFiles = async (files) => {
    const handleShowSnackbar = () => {
      showSnackbar(formatMessage({ id: 'wrong.msg' }))
    }
    const isInProcess = await checkSubscribersImport({ handleShowSnackbar })
    if (isInProcess) {
      showSnackbar(
        formatMessage({
          id: 'subscribers.import.importInProcess.errorMessage',
        }),
      )
      return
    }
    // initialize()
    let csvFile = files[0]
    if (!csvFile) {
      const errorMessage = formatMessage({
        id: 'subscribers.import.csv.invalid.file',
      })
      showSnackbar(errorMessage)
      dispatch(cancelImportThunk({ errorMessage }))
    } else {
      try {
        const { name, size } = csvFile
        const bytesToMB = size / 1024 / 1024
        validationMaxCSVSize(bytesToMB, formatMessage)

        let reader = new FileReader()
        reader.addEventListener('load', async function (e) {
          const csvDataString = e.target.result
          const rows = csvDataString.split(/\r\n|\n/)
          if (rows.length > LIMIT_CSV_IMPORT && mes_3653_limit_import_csv_300) {
            const limitedRows = rows.slice(0, LIMIT_CSV_IMPORT)
            const limitedCsvDataString = limitedRows.join('\n')

            const blob = new Blob([limitedCsvDataString], { type: 'text/csv' })
            const newSizeInBytes = blob.size

            setIsLimit(true)
            setDataLimitSize({
              name: name,
              size: newSizeInBytes,
              csvDataString: limitedCsvDataString,
            })
          } else handleUploadCSV(name, size, csvDataString)
        })
        reader.readAsText(csvFile)
      } catch (err) {
        if (typeof err === 'string') showSnackbar(err)
      }
    }
  }

  return (
    <>
      <DropZone onDrop={acceptedFiles} accept={'.csv'} multiple={false}>
        {({ getRootProps, getInputProps }) => {
          return (
            <DropZoneAreaBox
              isDisplayCareCheck={isDisplayCareCheck}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              toggleDialogs={toggleDialogs}
            />
          )
        }}
      </DropZone>
      {isLimit && mes_3653_limit_import_csv_300 && (
        <SubmitDialog
          id={`dialog-cancel-expiration-timeline`}
          open={isLimit}
          type={SECONDARY}
          title={formatMessage({
            id: 'subscribers.import.csv.limit.title',
          })}
          description={formatMessage({
            id: 'subscribers.import.csv.limit.description',
          })}
          submitText={formatMessage({
            id: 'proceed.label',
          })}
          onSubmit={() =>
            handleUploadCSV(
              dataLimitSize.name,
              dataLimitSize.size,
              dataLimitSize.csvDataString,
            )
          }
          closeText={formatMessage({
            id: 'text.close',
          })}
          onClose={() => setIsLimit(false)}
        />
      )}
    </>
  )
}

export default CSVDropZoneBox
